import { ACCOUNT_ROLES_NEW } from '@consts';

const { ADMIN, ONLY_VIEW, VIEW_AND_EDIT, CONNECTION_MANAGEMENT } =
    ACCOUNT_ROLES_NEW;

// ─────────────────────────────────────────────────────────────────────────────
// Role 해석 (판정 축) — 계약: React repo docs/permission/spec/role-resolution.md.
//
// React utils/roleResolution.ts의 미러. Vue에는 멤버 role 표시 화면이 없어서
// 판정(resolveLegacyRole — ROLE_PERMISSION_MAP이 읽을 legacy 배열)만 둔다.
// 표시 축(resolveRoleRow / roleRowFromLegacy)은 React 쪽 모듈 참조.
// ─────────────────────────────────────────────────────────────────────────────

/** 죽은 updateMember 액션이 account.role에 문자열을 쓰던 시절의 방어. */
const toArray = (role) => (Array.isArray(role) ? role : role ? [role] : []);

/**
 * 비-agency 워크스페이스의 legacy 정규화 — connection 개념이 노출되지 않는
 * 곳에서는 view_and_edit이 곧 connection 권한을 포함한다.
 * (getRefinedRollByAgentUsage에서 기본값 로직을 뺀 것)
 */
export const normalizeLegacyRole = ({ role, isAgency }) => {
    const isLimitedViewAndEdit =
        role.includes(VIEW_AND_EDIT) && !role.includes(CONNECTION_MANAGEMENT);

    if (!isAgency && isLimitedViewAndEdit) {
        return [...role, CONNECTION_MANAGEMENT];
    }

    return role;
};

/**
 * built-in key → 판정용 legacy 배열. BE getLegacyPermissions 미러.
 * full view_and_edit이 connection_management를 **무조건** 포함하므로
 * (agency/비-agency 동일) 이 변환은 normalizeLegacyRole을 탈 필요가 없다.
 */
const LEGACY_BY_BUILT_IN_KEY = {
    admin: [ADMIN],
    view_and_edit: [VIEW_AND_EDIT, CONNECTION_MANAGEMENT],
    view_and_edit_without_connection_management: [VIEW_AND_EDIT],
    view_only: [ONLY_VIEW],
};

/**
 * role row → 판정용 legacy 배열.
 *
 * custom은 무조건 view_only — workspace.role로 fall-through하지 않는다.
 * bulk PATCH가 custom 배정 시 이전 legacy permissions를 보존하므로(api.md)
 * custom role 멤버의 role 배열에 ['admin'] 같은 옛 권한이 남아 있을 수 있다.
 */
export const legacyFromRoleRow = (row) => {
    if (row.type === 'custom' || row.key == null) {
        return [ONLY_VIEW];
    }

    return LEGACY_BY_BUILT_IN_KEY[row.key];
};

/**
 * 판정용 — ROLE_PERMISSION_MAP 화이트리스트가 읽을 legacy 배열.
 *
 * 1. flag ON + roleId가 row로 매칭 → key 환산 (custom은 view_only 확정)
 * 2. legacy 배열이 있으면 그대로 (+ 비-agency 정규화) — flag OFF의 custom
 *    유저는 배정 시 저장된 ["view_only"]가 여기서 작동한다
 * 3. 둘 다 비면: role/roleId가 비는 건 backoffice 유저뿐이라는 BE 확인
 *    (2026-09-29)에 따라 backoffice admin → admin, 그 외 → view_only.
 *    기존의 "빈 배열 → ['admin']" 승격을 대체하는 분기다.
 */
export const resolveLegacyRole = ({
    capabilityBasedPermissions,
    roleId,
    role,
    roles,
    isAgency,
    isBackofficeAdmin,
}) => {
    if (capabilityBasedPermissions && roleId != null) {
        const row = roles.find((r) => r.id === roleId);
        if (row) {
            return legacyFromRoleRow(row);
        }
    }

    const legacy = toArray(role);
    if (legacy.length > 0) {
        return normalizeLegacyRole({ role: legacy, isAgency });
    }

    return isBackofficeAdmin ? [ADMIN] : [ONLY_VIEW];
};
